/**
 * Expense Export Utilities
 * Handles CSV and PDF export for expense data
 */

export const exportToCSV = (data, filename = 'expenses') => {
    if (!data || data.length === 0) {
        alert('No data to export');
        return;
    }

    // Define CSV headers
    const headers = [
        'Expense No',
        'Date',
        'Type',
        'Category',
        'Sub-Category',
        'Description',
        'Amount',
        'Vehicle',
        'Driver',
        'Owner',
        'Payment Method',
        'Invoice Number',
        'Vendor',
        'Approval Status',
        'Approved By',
        'Approval Date',
        'Reimbursement Status',
        'Created By',
        'Created At'
    ];

    // Convert data to CSV rows
    const csvRows = data.map(expense => [
        expense.expense_no || '',
        formatDate(expense.expense_date) || '',
        expense.expense_type || '',
        expense.category_name || '',
        expense.sub_category_name || '',
        `"${(expense.description || '').replace(/"/g, '""')}"`,
        expense.amount || 0,
        expense.plate_no || '',
        expense.driver_name || '',
        expense.owner_name || '',
        expense.payment_method || '',
        expense.invoice_number || expense.receipt_number || '',
        expense.vendor_name || '',
        expense.approval_status || '',
        expense.approved_by_name || '',
        formatDate(expense.approval_date) || '',
        expense.reimbursement_status || '',
        expense.created_by_name || '',
        formatDateTime(expense.created_at) || ''
    ]);

    // Combine headers and rows
    const csvContent = [
        headers.join(','),
        ...csvRows.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportReportToCSV = (reportData, reportType, filename) => {
    if (!reportData || (Array.isArray(reportData) && reportData.length === 0)) {
        alert('No data to export');
        return;
    }

    let headers = [];
    let rows = [];

    switch (reportType) {
        case 'weekly':
        case 'monthly':
            if (reportData.by_category) {
                headers = ['Category', 'Total Amount', 'Expense Count', 'Average Amount'];
                rows = reportData.by_category.map(item => [
                    item.category_name,
                    item.total_amount,
                    item.expense_count,
                    item.avg_amount || 0
                ]);
            }
            break;

        case 'vehicle':
            headers = ['Vehicle', 'Total Amount', 'Expense Count', 'Average Amount'];
            rows = reportData.map(item => [
                item.plate_no,
                item.total_amount,
                item.expense_count,
                item.avg_amount || 0
            ]);
            break;

        case 'owner':
            headers = ['Owner', 'Total Amount', 'Vehicle Count', 'Expense Count'];
            rows = reportData.map(item => [
                item.owner_name,
                item.total_amount,
                item.vehicle_count,
                item.expense_count
            ]);
            break;

        case 'trends':
            headers = ['Period', 'Total Amount', 'Vehicle Amount', 'Office Amount'];
            rows = reportData.map(item => [
                item.period,
                item.total_amount,
                item.vehicle_amount,
                item.office_amount
            ]);
            break;

        case 'approval':
            if (reportData.by_category) {
                headers = ['Category', 'Approved', 'Pending', 'Rejected', 'Total'];
                rows = reportData.by_category.map(item => [
                    item.category_name,
                    item.approved_amount || 0,
                    item.pending_amount || 0,
                    item.rejected_amount || 0,
                    (parseFloat(item.approved_amount || 0) + parseFloat(item.pending_amount || 0) + parseFloat(item.rejected_amount || 0))
                ]);
            }
            break;

        default:
            alert('Unsupported report type');
            return;
    }

    // Create CSV content
    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportBudgetsToCSV = (budgets, filename = 'expense_budgets') => {
    if (!budgets || budgets.length === 0) {
        alert('No budgets to export');
        return;
    }

    const headers = [
        'Year',
        'Week',
        'Category',
        'Budgeted Amount',
        'Spent Amount',
        'Remaining',
        'Percentage Used',
        'Status',
        'Notes'
    ];

    const rows = budgets.map(budget => {
        const remaining = parseFloat(budget.budgeted_amount) - parseFloat(budget.spent_amount || 0);
        const percentage = parseFloat(budget.spent_percentage || 0);
        const status = percentage >= 100 ? 'Exceeded' : percentage >= 80 ? 'Warning' : 'On Track';

        return [
            budget.budget_year,
            budget.budget_week,
            budget.category_name,
            budget.budgeted_amount,
            budget.spent_amount || 0,
            remaining,
            `${percentage.toFixed(1)}%`,
            status,
            `"${(budget.notes || '').replace(/"/g, '""')}"`
        ];
    });

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const exportCategoryStatsToCSV = (stats, filename = 'category_analytics') => {
    if (!stats || stats.length === 0) {
        alert('No data to export');
        return;
    }

    const headers = [
        'Category',
        'Type',
        'Total Amount',
        'Expense Count',
        'Average Amount',
        'Percentage of Total'
    ];

    const total = stats.reduce((sum, cat) => sum + parseFloat(cat.total_amount || 0), 0);

    const rows = stats.map(cat => [
        cat.category_name,
        cat.expense_type,
        cat.total_amount,
        cat.expense_count,
        cat.avg_amount || 0,
        `${total > 0 ? ((parseFloat(cat.total_amount) / total) * 100).toFixed(1) : 0}%`
    ]);

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Helper functions
const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};
